import { addDays, differenceInDays, isEqual } from 'date-fns';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { ResponsiveLine } from '@nivo/line';
import { useMemo } from 'react';

import { SalesByTopChannelsLegend } from './legends/SalesByTopChannelsLegend';
import { SalesByTopChannelsTooltip } from './tooltips/SalesByTopChannelsTooltip';
import { AnalyticsItemBox } from '../pages/Analytics/AnalyticsItemBox';
import { EmptyChart } from '../pages/Analytics/EmptyChart';

import { graphCurrencyFormatter, graphUnitsFormatter, INTEGERS_ONLY } from './utils';
import { graphColors, graphTheme } from './consts';
import { formatDateGraphXAxis } from '../../utils/analytics-granularity';
import { shouldShowYearsInXAxis } from '../../utils/analytics';

import styles from './graph.module.scss';
import { analyticsTopSellersUtils } from '../../utils/analytics/top-sellers.utils';

export function SalesByTopChannelsGraph(props) {
  const { title, metric, height, data, currentCurrency, isGmvMetric, currentOrderType } = props;

  const dataKey = analyticsTopSellersUtils.getDataKeyFromCurrentOrderType(currentOrderType);
  const uiData = data[dataKey];

  const xAxisLabelByDate = useMemo(() => {
    let result = {};

    if (uiData?.[0]?.data?.length) {
      const dateGraphPoints = uiData[0].data;

      const startDate = new Date(dateGraphPoints[0].fullDate);
      const endDate = new Date(dateGraphPoints.at(-1).fullDate);
      const showYears = shouldShowYearsInXAxis(startDate, endDate);

      let daysDiff = Math.round(differenceInDays(endDate, startDate) / 13);
      if (daysDiff === 0) {
        daysDiff = 1;
      }

      let nextDate = new Date(startDate);
      let nextShownDate = new Date(startDate);

      dateGraphPoints.forEach((graphPoint) => {
        if (isEqual(nextShownDate, nextDate)) {
          nextShownDate = addDays(nextShownDate, daysDiff);
          result[graphPoint.x] = formatDateGraphXAxis(nextDate, showYears);
          if (daysDiff > 2 && differenceInDays(endDate, nextShownDate) < daysDiff) {
            nextShownDate = endDate;
          }
        }

        nextDate = addDays(nextDate, 1);
      });

      result[formatDateGraphXAxis(endDate, showYears)] = formatDateGraphXAxis(endDate, showYears);
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiData]);

  return (
    <AnalyticsItemBox title={title} metric={metric}>
      <div className={cn(styles.graphWrapper, styles.stackedLineGraph)} style={{ height: height || 400 }}>
        {uiData.length > 0 ? (
          <ResponsiveLine
            margin={{
              top: 10,
              right: 20,
              bottom: 60,
              left: 80,
            }}
            animate
            curve="monotoneX"
            data={uiData}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: 'auto' }}
            yFormat={(value) => (isGmvMetric ? graphCurrencyFormatter(value, currentCurrency) : value)}
            pointLabelYOffset={0}
            axisLeft={{
              format: (value) =>
                isGmvMetric
                  ? graphCurrencyFormatter(value, currentCurrency, INTEGERS_ONLY)
                  : graphUnitsFormatter(value, INTEGERS_ONLY),
              tickSize: 0,
            }}
            axisBottom={{
              format: (date) => xAxisLabelByDate[date] ?? '',
              tickSize: 0,
            }}
            colors={graphColors}
            theme={graphTheme}
            enableSlices="x"
            enablePoints={false}
            enableGridX={false}
            enableGridY={true}
            sliceTooltip={SalesByTopChannelsTooltip({
              data,
              isGmvMetric,
              currency: currentCurrency,
            })}
            layers={['grid', 'axes', 'crosshair', 'lines', 'slices', SalesByTopChannelsLegend()]}
          />
        ) : (
          <EmptyChart />
        )}
      </div>
    </AnalyticsItemBox>
  );
}

SalesByTopChannelsGraph.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    gmv: PropTypes.arrayOf(PropTypes.object).isRequired,
    units: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  metric: PropTypes.string,
  height: PropTypes.number,
  currentCurrency: PropTypes.string,
  isGmvMetric: PropTypes.bool.isRequired,
};
